/**
 * ADO #300 — Custom Condition Logic Is Met (Salesforce-style).
 *
 * Two pure helpers shared by Decision and Loop editors:
 *
 *   validateCustomLogic(expression, conditionCount)
 *     Returns { ok: true } or { ok: false, error: string }.
 *     Walks the expression character-by-character checking:
 *       - parentheses balanced
 *       - tokens are only AND / OR / NOT (case-insensitive),
 *         positive integers, or parens
 *       - every integer references an existing condition row (1..N)
 *       - the AST shape makes sense (operands and operators alternate)
 *     Empty expression is treated as not-yet-typed (ok: true) so the
 *     student doesn't see an immediate red error before typing.
 *
 *   substituteCustomLogic(expression, renderedConditions, language)
 *     Returns the codegen-ready boolean expression. Replaces each
 *     positive integer N with `(<expr-of-condition-N>)` and AND/OR/NOT
 *     with the language's logical operators (`&&`/`||`/`!` for
 *     C/C++/Java; `and`/`or`/`not` for Python).
 *     Out-of-range numbers are left alone — `validateCustomLogic`
 *     surfaces the error before codegen.
 *
 * `renderedConditions` is the array of strings that
 * `conditionsToRawExpression` (or its inline equivalent) would emit
 * for each row in the same language, e.g.
 *   ['year % 4 == 0', 'year % 100 != 0', 'year % 400 == 0']
 */

const LOGIC_KEYWORDS = new Set(['AND', 'OR', 'NOT']);

const PYTHON_OPS = { AND: 'and', OR: 'or', NOT: 'not' };
const C_OPS = { AND: '&&', OR: '||', NOT: '!' };

const isPython = (lang) => (lang || '').toLowerCase() === 'python';

/**
 * Tokenise the expression. Returns an array of token objects:
 *   { type: 'paren', value: '(' | ')', pos }
 *   { type: 'op',    value: 'AND' | 'OR' | 'NOT', pos }
 *   { type: 'num',   value: 3, pos }
 *   { type: 'error', value: <bad char>, pos }
 */
const tokenise = (expr) => {
  const tokens = [];
  if (!expr) return tokens;
  const s = expr.trim();
  const len = s.length;
  let i = 0;
  while (i < len) {
    const c = s[i];
    if (c === ' ' || c === '\t' || c === '\n') { i += 1; continue; }
    if (c === '(' || c === ')') {
      tokens.push({ type: 'paren', value: c, pos: i });
      i += 1;
      continue;
    }
    if (/\d/.test(c)) {
      let j = i;
      while (j < len && /\d/.test(s[j])) j += 1;
      tokens.push({ type: 'num', value: parseInt(s.slice(i, j), 10), pos: i });
      i = j;
      continue;
    }
    if (/[A-Za-z]/.test(c)) {
      let j = i;
      while (j < len && /[A-Za-z]/.test(s[j])) j += 1;
      const word = s.slice(i, j).toUpperCase();
      if (LOGIC_KEYWORDS.has(word)) {
        tokens.push({ type: 'op', value: word, pos: i });
      } else {
        tokens.push({ type: 'error', value: s.slice(i, j), pos: i });
      }
      i = j;
      continue;
    }
    tokens.push({ type: 'error', value: c, pos: i });
    i += 1;
  }
  return tokens;
};

export const validateCustomLogic = (expression, conditionCount) => {
  if (!expression || !expression.trim()) return { ok: true };

  const tokens = tokenise(expression);

  // Reject any token the lexer rejected.
  const lexErr = tokens.find((t) => t.type === 'error');
  if (lexErr) {
    return { ok: false, error: `Unexpected token "${lexErr.value}" — use AND, OR, NOT, parentheses, and condition numbers.` };
  }

  // Parens balance.
  let depth = 0;
  for (const t of tokens) {
    if (t.type === 'paren') {
      depth += t.value === '(' ? 1 : -1;
      if (depth < 0) return { ok: false, error: 'Unbalanced parentheses: a closing ) has no matching (.' };
    }
  }
  if (depth !== 0) return { ok: false, error: 'Unbalanced parentheses: an opening ( has no matching ).' };

  // Numbers reference existing rows.
  for (const t of tokens) {
    if (t.type === 'num') {
      if (t.value < 1 || t.value > conditionCount) {
        return { ok: false, error: `Condition ${t.value} doesn't exist — there ${conditionCount === 1 ? 'is' : 'are'} ${conditionCount} condition${conditionCount === 1 ? '' : 's'}.` };
      }
    }
  }

  // Sequence sanity. Walk a tiny state machine: expect an operand, then
  // an operator, alternating. Parens flip back to operand-expected. NOT
  // can appear in operand position.
  let expectOperand = true;
  let consumedAny = false;
  for (const t of tokens) {
    if (t.type === 'paren' && t.value === '(') {
      if (!expectOperand) return { ok: false, error: 'Missing AND/OR before "(".' };
      // still operand-expected after '('
      continue;
    }
    if (t.type === 'paren' && t.value === ')') {
      if (expectOperand) return { ok: false, error: 'Missing operand before ")".' };
      // operator-expected stays
      continue;
    }
    if (t.type === 'op' && t.value === 'NOT') {
      if (!expectOperand) return { ok: false, error: 'Missing AND/OR before NOT.' };
      // still expecting operand after NOT
      continue;
    }
    if (t.type === 'op') {  // AND / OR
      if (expectOperand) return { ok: false, error: `Missing operand before ${t.value}.` };
      expectOperand = true;
      continue;
    }
    if (t.type === 'num') {
      if (!expectOperand) return { ok: false, error: 'Missing AND/OR between conditions.' };
      expectOperand = false;
      consumedAny = true;
      continue;
    }
  }
  if (expectOperand && consumedAny) {
    return { ok: false, error: 'Trailing operator — expression ends without an operand.' };
  }
  if (!consumedAny) {
    return { ok: false, error: 'No conditions referenced. Use 1, 2, … to reference rows.' };
  }
  return { ok: true };
};

export const substituteCustomLogic = (expression, renderedConditions, language) => {
  if (!expression) return '';
  const tokens = tokenise(expression);
  if (tokens.some((t) => t.type === 'error')) return expression;
  const ops = isPython(language) ? PYTHON_OPS : C_OPS;
  const out = [];
  let prevType = null;
  for (const t of tokens) {
    if (t.type === 'paren') {
      out.push(t.value);
      prevType = t.type;
      continue;
    }
    if (t.type === 'op') {
      if (t.value === 'NOT') {
        // For C/C++/Java emit `!`; for Python emit `not ` (space-separated).
        out.push(isPython(language) ? `${ops.NOT} ` : ops.NOT);
      } else {
        // Pad logical operators with spaces; trim duplicates later.
        out.push(` ${ops[t.value]} `);
      }
      prevType = t.type;
      continue;
    }
    if (t.type === 'num') {
      const idx = t.value - 1;
      const cond = renderedConditions[idx];
      out.push(cond ? `(${cond})` : String(t.value));
      prevType = t.type;
      continue;
    }
  }
  return out.join('').replace(/\s+/g, ' ').trim();
};
